/**
 * Edit Entry Modal Component
 *
 * Modal for editing existing accomplishment entries.
 * Uses the same polished design as AddEntryModal for consistency.
 */

import { useState, useEffect, useRef } from 'react';
import { X, Sparkles, Clock, Save, Smile } from 'lucide-react';
import { useUpdateEntry, type Entry, type Category } from '@have-done/shared';
import { CategorySelector } from './CategorySelector';
import { SatisfactionRating } from './SatisfactionRating';
import { useToast } from './Toast';

interface EditEntryModalProps {
  entry: Entry | null;
  isOpen: boolean;
  onClose: () => void;
}

export function EditEntryModal({ entry, isOpen, onClose }: EditEntryModalProps) {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<Category | null>(null);
  const [satisfaction, setSatisfaction] = useState<number | null>(null);
  const [timeSpent, setTimeSpent] = useState<string>('');
  const [isBigWin, setIsBigWin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateEntry = useUpdateEntry();
  const { showSuccess, showError } = useToast();
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  // Populate form when entry changes
  useEffect(() => {
    if (entry) {
      setContent(entry.content);
      setCategory(entry.category);
      setSatisfaction(entry.satisfaction ?? null);
      setTimeSpent(entry.timeSpentMinutes?.toString() ?? '');
      setIsBigWin(entry.isBigWin);
    }
  }, [entry]);

  // Focus content when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => contentRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSubmitting, onClose]);

  const canSubmit = content.trim().length > 0 && category !== null && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !entry || !category) return;

    setIsSubmitting(true);
    try {
      await updateEntry({
        id: entry._id as any,
        content: content.trim(),
        category,
        satisfaction: satisfaction ?? undefined,
        timeSpentMinutes: timeSpent ? parseInt(timeSpent, 10) : undefined,
        isBigWin,
      });

      showSuccess('Entry updated!');
      onClose();
    } catch (error) {
      console.error('Failed to update entry:', error);
      showError('Failed to save changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !entry) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={isSubmitting ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-lg rounded-xl bg-bg-primary shadow-warm-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light">
          <h2 id="edit-modal-title" className="text-lg font-semibold text-text-primary">
            Edit entry
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            className="
              p-1 rounded-lg text-text-tertiary
              hover:text-text-primary hover:bg-bg-secondary
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Content */}
          <div>
            <label htmlFor="edit-content" className="sr-only">
              What did you accomplish?
            </label>
            <textarea
              ref={contentRef}
              id="edit-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What did you accomplish?"
              disabled={isSubmitting}
              rows={3}
              className="
                w-full rounded-lg border border-border-light bg-bg-primary px-4 py-3
                text-text-primary placeholder-text-tertiary
                resize-none
                transition-colors
                focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            />
          </div>

          {/* Category */}
          <div>
            <p className="mb-2 text-sm font-medium text-text-secondary">Category</p>
            <CategorySelector value={category} onChange={setCategory} disabled={isSubmitting} />
          </div>

          {/* Optional Details Section */}
          <div className="space-y-5 pt-2">
            {/* Section Header */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-secondary">Details</span>
              <div className="flex-1 h-px bg-border-light" aria-hidden="true" />
            </div>

            {/* Satisfaction Rating */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                <Smile className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                How did it feel?
              </label>
              <SatisfactionRating
                value={satisfaction}
                onChange={setSatisfaction}
                disabled={isSubmitting}
                showLabels
              />
            </div>

            {/* Time Spent & Big Win Row */}
            <div className="flex flex-wrap items-start gap-6">
              {/* Time spent */}
              <div className="space-y-2">
                <label
                  htmlFor="edit-timeSpent"
                  className="flex items-center gap-2 text-sm font-medium text-text-secondary"
                >
                  <Clock className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  Time spent
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    id="edit-timeSpent"
                    value={timeSpent}
                    onChange={(e) => setTimeSpent(e.target.value)}
                    min="1"
                    max="1440"
                    placeholder="30"
                    disabled={isSubmitting}
                    className="
                      w-20 rounded-lg border border-border-light bg-bg-primary px-3 py-2
                      text-text-primary placeholder-text-tertiary text-center
                      transition-shadow transition-colors duration-150
                      focus:border-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(107,91,149,0.15)]
                      disabled:opacity-50 disabled:cursor-not-allowed
                      [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                    "
                  />
                  <span className="text-sm text-text-tertiary">minutes</span>
                </div>
              </div>

              {/* Big win toggle */}
              <div className="space-y-2">
                <span className="flex items-center gap-2 text-sm font-medium text-text-secondary">
                  <Sparkles
                    className={`h-4 w-4 transition-colors ${isBigWin ? 'text-accent' : 'text-text-tertiary'}`}
                    aria-hidden="true"
                  />
                  Big win?
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isBigWin}
                  onClick={() => setIsBigWin(!isBigWin)}
                  disabled={isSubmitting}
                  className={`
                    group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full
                    transition-all duration-200 ease-out
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${isBigWin
                      ? 'bg-gradient-to-r from-accent to-accent-light shadow-inner'
                      : 'bg-border hover:bg-border-dark'
                    }
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-flex h-5 w-5 rounded-full items-center justify-center
                      bg-white shadow-md ring-0 transition-all duration-200 ease-out
                      ${isBigWin ? 'translate-x-6' : 'translate-x-1'}
                      ${isBigWin ? 'scale-100' : 'group-hover:scale-105'}
                    `}
                    style={{ marginTop: '4px' }}
                  >
                    {isBigWin && (
                      <Sparkles className="h-3 w-3 text-accent" aria-hidden="true" />
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="
                flex-1 px-4 py-2.5 rounded-lg
                text-sm font-medium text-text-primary
                bg-bg-secondary hover:bg-bg-tertiary
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="
                flex-1 inline-flex items-center justify-center gap-2
                px-4 py-2.5 rounded-lg
                text-sm font-medium text-white
                bg-primary hover:bg-primary-dark
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <Save className="h-4 w-4" aria-hidden="true" />
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
